import type {
  FiatStepResponse,
  Quote,
  QuoteLimits,
} from '@fun-xyz/fiat-contract/types'
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'

import { sendAuthedFiatTransition } from '~/domains/fiat/authedTransport'
import type { EntryCta } from '~/domains/fiat/entryCta'
import {
  enteredStep,
  selectEntryCta,
  submittableTransition,
} from '~/domains/fiat/entryCta'
import { redactFiatBodyForLog } from '~/domains/fiat/fiatLogRedactor'
import type {
  FiatQuoteMaximum,
  FiatQuoteMinimum,
  FiatRoutingOverrides,
  QuoteGeo,
  QuoteRequest,
} from '~/domains/fiat/quoteRequest'
import {
  QUOTE_ENDPOINT,
  isQuotableAmount,
  isQuotableGeo,
  isSupportedQuoteChainId,
  isUnpriceableUsGeo,
  quoteMaximumFromError,
  quoteMinimumFromError,
  routingOverridesKey,
} from '~/domains/fiat/quoteRequest'
import {
  type RouteIdentity,
  readRouteLimits,
  recordRouteLimits,
  routeLimitsKey,
} from '~/domains/fiat/routeLimits'
import type { FunLogger } from '~/utils/funLogger'
import { randomUuid } from '~/utils/randomUuid'
import { useDebouncedValue } from '../useDebouncedValue'
import type { FiatFlow } from './useFiatTransitions'

const DEFAULT_DEBOUNCE_MS = 400
const QUOTE_QUERY_SCOPE = ['fiat', 'quote'] as const
/** Largest head start for a replacement to land before the old quote dies. */
const QUOTE_REFRESH_BUFFER_MS = 5_000

export interface UseFiatQuoteParams {
  apiKey: string
  userId?: string
  logger: FunLogger
  flow: Pick<
    FiatFlow,
    'submit' | 'receive' | 'isInFlight' | 'identityAssertionProvider'
  >
  amount: string
  /**
   * ISO 4217 the buyer pays in, resolved from their region. Undefined while
   * that lookup is in flight; the request requires it, so no quote is sent
   * until it lands.
   */
  currency: string | undefined
  paymentMethod: string
  cryptoCurrencyCode: string
  chainId: string
  /**
   * Where the buyer is, in the geo lookup's own field names. Optional only
   * because that lookup is async — the request requires it, so no quote is
   * sent until it resolves.
   */
  geo?: QuoteGeo
  /**
   * Test tooling; see {@link FiatRoutingOverrides}. Prefixed so a host does
   * not reach it by tab-completion: production backends refuse the quote.
   */
  unstable_routingOverrides?: FiatRoutingOverrides
  enabled?: boolean
  debounceMs?: number
}

export interface UseFiatQuoteResult {
  /** Provider selected by the fresh response, absent while repricing. */
  provider?: FiatStepResponse['provider']
  /** The last quote remains available while its replacement loads. */
  quote?: Quote
  /** What the buyer may deposit at their current tier; absent until quoted. */
  limits?: QuoteLimits
  /** Only a fresh quote for the current inputs can be submitted. */
  isQuoteFresh: boolean
  isQuoting: boolean
  entryCta: EntryCta
  submitQuote: () => void
  retryQuote: () => void
  /**
   * Forgets the price being shown, so the next ask fetches one. A quoteRef the
   * server has lost is still cached here, and still inside its stated
   * lifetime, so nothing else would go and get a live one.
   */
  dropQuote: () => void
  quoteError?: unknown
  /** Actionable lower bound returned when every applicable provider refuses the amount. */
  minimum?: FiatQuoteMinimum
  /** The ceiling a refusal reported; this buyer's own, not the corridor's. */
  maximum?: FiatQuoteMaximum
}

/** Fetches and submits the quote shown on the amount screen. */
export function useFiatQuote({
  apiKey,
  userId = '',
  logger,
  flow,
  amount,
  currency,
  paymentMethod,
  cryptoCurrencyCode,
  chainId,
  geo,
  unstable_routingOverrides: routingOverrides,
  enabled = true,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseFiatQuoteParams): UseFiatQuoteResult {
  const queryClient = useQueryClient()
  // A quoteRef is transaction state, so it cannot cross a visible flow boundary.
  const [flowScope] = useState(randomUuid)
  const { debounced: settledAmount, isSettled } = useDebouncedValue(
    amount,
    debounceMs,
  )
  const hasSupportedChain = isSupportedQuoteChainId(chainId)

  const queryKey = [
    ...QUOTE_QUERY_SCOPE,
    flowScope,
    apiKey,
    userId,
    settledAmount,
    currency ?? '',
    paymentMethod,
    cryptoCurrencyCode,
    chainId,
    // Keyed as well as gated, so a location that lands after the amount was
    // typed re-quotes rather than leaving the first answer standing.
    geo?.alpha2 ?? '',
    geo?.region ?? '',
    // A different provider set is a different price, so it re-quotes.
    routingOverridesKey(routingOverrides),
  ] as const
  // ENG-6302. A maximum belongs to the route, not to the amount, so once this
  // session has been told one there is nothing to ask about an amount over it:
  // the answer is already known and the screen can name it now rather than
  // after a round trip. The record only ever narrows within a session and is
  // dropped whenever something could have widened it, so this cannot hold a
  // buyer under a ceiling the server has since raised.
  const route: RouteIdentity = {
    apiKey,
    userId,
    currency,
    paymentMethod,
    cryptoCurrencyCode,
    chainId,
    geo,
    routingOverrides,
  }
  const knownLimits = readRouteLimits(queryClient, route)
  const isKnownAboveMaximum =
    knownLimits !== undefined &&
    knownLimits.currency === currency &&
    Number(settledAmount) > Number(knownLimits.max)

  const canFetch =
    enabled &&
    !flow.isInFlight &&
    isSettled &&
    isQuotableAmount(settledAmount) &&
    hasSupportedChain &&
    isQuotableGeo(geo) &&
    currency !== undefined &&
    !isKnownAboveMaximum

  // The request goes out and the server refuses it — the buyer at least gets
  // an answer — but no geo provider naming a state for a US buyer is a gap in
  // the lookup, not something the buyer did.
  const isStatelessUs = isUnpriceableUsGeo(geo)
  useEffect(() => {
    if (!isStatelessUs) {
      return
    }
    logger.warn('connect-core:fiat:quote_geo_without_state', { alpha2: 'US' })
  }, [logger, isStatelessUs])

  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      try {
        if (!isQuotableAmount(settledAmount)) {
          throw new Error('Quote request has an invalid amount')
        }
        if (!isSupportedQuoteChainId(chainId)) {
          throw new Error('Quote request has an unsupported chain ID')
        }
        if (geo === undefined || !isQuotableGeo(geo)) {
          throw new Error('Quote request has no usable geo')
        }
        if (currency === undefined) {
          throw new Error('Quote request has no currency')
        }
        if (routingOverrides !== undefined) {
          // A provider decision made ahead of the backend's own; without this
          // a trace cannot tell a pinned run from ordinary routing.
          logger.info('connect-core:fiat:quote_routing_overridden', {
            providers: routingOverrides.providers,
          })
        }
        const response = await sendAuthedFiatTransition<FiatStepResponse>(
          {
            apiKey,
            logger,
            signal,
            redactBodyForLog: redactFiatBodyForLog,
            params: {
              endpoint: QUOTE_ENDPOINT,
              body: {
                fiatCurrency: currency,
                fiatAmount: settledAmount,
                cryptoCurrencyCode,
                chainId,
                paymentMethodId: paymentMethod,
                geo: {
                  alpha2: geo.alpha2,
                  ...(geo.region === undefined ? {} : { region: geo.region }),
                },
                ...(routingOverrides === undefined ? {} : { routingOverrides }),
              } satisfies QuoteRequest,
              idempotencyKey: randomUuid(),
            },
          },
          flow.identityAssertionProvider,
        )
        assertUsableStep(response, {
          currency,
          paymentMethod,
          cryptoCurrencyCode,
        })
        return response
      } catch (caught) {
        if (!signal.aborted) {
          logger.warn('connect-core:fiat:quote_failed', {
            paymentMethod,
            ...(routingOverrides === undefined
              ? {}
              : { providers: routingOverrides.providers }),
            cause: caught instanceof Error ? caught.message : String(caught),
          })
        }
        throw caught
      }
    },
    enabled: canFetch,
    placeholderData: keepPreviousData,
    retry: false,
    // Expiry stated in TanStack's own vocabulary: a quote is "stale" exactly
    // when it has run out, and the interval replaces it a buffer early.
    // Both are load-bearing. `staleTime` never fetches on its own — it only
    // gates the refetch triggers below. `refetchInterval` fetches but cannot
    // rescue a cached quote that is already expired when the screen mounts,
    // because an interval of `0` disables the timer outright.
    staleTime: (cached) => entryStaleTime(cached.state.data),
    refetchInterval: (cached) => {
      const remaining = remainingLifetime(cached.state.data)
      if (remaining === undefined) {
        return false
      }
      return remaining > 0 ? remaining : false
    },
    refetchOnMount: true,
    refetchOnReconnect: true,
    // A backgrounded RN app never reports a focus change (`focusManager`
    // is not wired to `AppState`), so this would only ever fire on web.
    refetchOnWindowFocus: false,
  })

  // Every query option decides whether a fetch *starts*; none stop one that
  // is already running. Disabling does not unsubscribe the observer either,
  // so TanStack's cancel-on-last-unsubscribe never fires here. When the flow
  // takes over or the chain goes unsupported the key does not change at all,
  // so without this a request nobody wants runs to completion against the
  // payment API.
  useEffect(() => {
    if (canFetch) {
      return
    }
    void queryClient.cancelQueries({ queryKey, exact: true })
  }, [canFetch, queryClient, ...queryKey])

  const queryResponse = query.data

  // `isStale` rather than a `Date.now()` read: TanStack owns a timer for it
  // (`timeUntilStale`) and re-renders when it flips, so freshness is a value
  // this render is subscribed to instead of one it peeks at. Because
  // `staleTime` is the quote's remaining lifetime, stale means spent.
  // A round is usable the moment it lands and has nothing to expire, so
  // staleness — which here means "the quote has run out" — cannot judge it.
  const freshQueryResponse =
    isSettled &&
    !query.isFetching &&
    !query.isPlaceholderData &&
    (!query.isStale || isRound(queryResponse)) &&
    queryResponse !== undefined
      ? queryResponse
      : undefined
  const currentResponse = freshQueryResponse
  const displayedResponse = currentResponse ?? queryResponse
  const quote =
    displayedResponse?.state.kind === 'QUOTE'
      ? displayedResponse.state.quote
      : undefined
  const limits =
    displayedResponse?.state.kind === 'QUOTE'
      ? displayedResponse.state.limits
      : undefined
  const isQuoteFresh = enabled && currentResponse !== undefined
  const entryCta = selectEntryCta(currentResponse)

  // The whole reason UNRECOGNIZED is its own branch: a step this build does
  // not know still renders, so without this the backend moving ahead of us is
  // invisible until someone files a ticket.
  useEffect(() => {
    if (entryCta.kind !== 'UNRECOGNIZED') {
      return
    }
    logger.warn('connect-core:fiat:unrecognized_entry_cta', {
      id: entryCta.control.cta.id,
      endpoint: entryCta.control.cta.endpoint,
      paymentMethod,
    })
  }, [entryCta, logger, paymentMethod])

  const submitQuote = useCallback(() => {
    if (!isQuoteFresh || flow.isInFlight || currentResponse === undefined) {
      return
    }
    // The round is already in hand: entering it is handing it over, and there
    // is no request to send.
    const step = enteredStep(entryCta)
    if (step !== undefined) {
      logger.info('connect-core:fiat:entry_round_entered', {
        state: step.state.kind,
        paymentMethod,
      })
      flow.receive(step)
      return
    }
    // Only a price can go stale, and a session round carries none — read
    // strictly, `expiryTime` calls it expired and the button does nothing.
    if ('quote' in currentResponse.state && isExpired(currentResponse)) {
      return
    }
    const transition = submittableTransition(entryCta)
    if (transition === undefined) {
      return
    }
    logger.info('connect-core:fiat:entry_cta_fired', {
      kind: entryCta.kind,
      endpoint: transition.endpoint,
      paymentMethod,
    })
    void flow.submit(transition)
  }, [currentResponse, entryCta, flow, isQuoteFresh, logger, paymentMethod])

  const dropQuote = useCallback(() => {
    queryClient.removeQueries({ queryKey, exact: true })
    // The key is what identifies the price to forget.
  }, [queryClient, ...queryKey])

  const retryQuote = useCallback(() => {
    if (!canFetch) {
      return
    }
    void query.refetch()
  }, [canFetch, query.refetch])

  const isQuoting =
    enabled &&
    hasSupportedChain &&
    isQuotableAmount(amount) &&
    (!isSettled || query.isFetching)
  const quoteError = isSettled ? (query.error ?? undefined) : undefined
  const minimum = quoteMinimumFromError(quoteError)
  const refusedMaximum = quoteMaximumFromError(quoteError)
  // The answer is the same whether the server just gave it or gave it earlier
  // in this session: this amount is over the route's maximum, and here is the
  // maximum. Without this the skipped request would take the bound notice and
  // the way back with it, which is the dead end the skip is meant to avoid.
  const maximum =
    refusedMaximum ??
    (isKnownAboveMaximum && knownLimits !== undefined
      ? { amount: knownLimits.max, currency: knownLimits.currency }
      : undefined)

  // Learned only from a refusal. A refusal's maximum is computed across every
  // candidate the route still had, so it describes the route. A priced quote's
  // `limits` is the committed provider's own range — `quoteLimitsFor` returns
  // that provider's ceiling, or the baseline, for the amount that selected it —
  // and the engine filters candidates by the requested amount before pricing.
  // Recording it here would let a provider chosen for a small amount suppress a
  // larger one another provider would have served.
  useEffect(() => {
    if (refusedMaximum === undefined) {
      return
    }
    recordRouteLimits(queryClient, route, {
      max: refusedMaximum.amount,
      currency: refusedMaximum.currency,
    })
    // `route` is rebuilt every render; its own fields are the identity.
  }, [
    queryClient,
    refusedMaximum?.amount,
    refusedMaximum?.currency,
    ...routeLimitsKey(route),
  ])

  return {
    provider: currentResponse?.provider,
    ...(quote === undefined ? {} : { quote }),
    ...(limits === undefined ? {} : { limits }),
    isQuoteFresh,
    isQuoting,
    entryCta,
    submitQuote,
    retryQuote,
    dropQuote,
    ...(quoteError === undefined ? {} : { quoteError }),
    ...(minimum === undefined ? {} : { minimum }),
    ...(maximum === undefined ? {} : { maximum }),
  }
}

/**
 * A round the endpoint answers with in place of a price — Banxa holds the
 * deposit on identity before it will quote at all. It is the flow's from here
 * on, so the request checks below do not apply to it — but the quote it
 * carries expires like any other, which is why `expiryTime` still reads one.
 */
function isRound(response: FiatStepResponse | undefined): boolean {
  return response !== undefined && response.state.kind !== 'QUOTE'
}

/** A quote-less round has no price to expire while its inputs are collected. */
function entryStaleTime(response: FiatStepResponse | undefined): number {
  return remainingLifetime(response) ?? (isRound(response) ? Infinity : 0)
}

/**
 * A quote has to answer the request that asked for it. A round answers no
 * request and is checked by nothing here — the flow owns it from here on.
 */
function assertUsableStep(
  response: FiatStepResponse,
  request: {
    currency: string
    paymentMethod: string
    cryptoCurrencyCode: string
  },
): void {
  if (response.state.kind !== 'QUOTE') {
    return
  }
  const { quote } = response.state
  if (
    quote.fiat.currency !== request.currency ||
    quote.paymentMethod !== request.paymentMethod ||
    quote.crypto.currency !== request.cryptoCurrencyCode
  ) {
    throw new Error('Quote response does not match the request')
  }
  if (isExpired(response)) {
    throw new Error('Quote response is already expired')
  }
}

/**
 * How long a response stays usable, less the head start. `undefined` when it
 * carries no usable expiry at all, which each caller reads differently.
 */
function remainingLifetime(
  response: FiatStepResponse | undefined,
): number | undefined {
  if (response === undefined) {
    return undefined
  }
  const expiresAt = expiryTime(response)
  if (expiresAt === undefined) {
    return undefined
  }
  const lifetime = expiresAt - Date.now()
  if (lifetime <= 0) {
    return 0
  }
  // A head start longer than the quote itself would pin this to zero, which
  // disables the refetch timer outright — so short-lived quotes get a
  // proportional one and still refresh ahead of expiry.
  const headStart = Math.min(QUOTE_REFRESH_BUFFER_MS, lifetime / 2)
  return lifetime - headStart
}

/**
 * Reads the clock, so it belongs only at a decision point — submitting, or
 * accepting a fetched response. Renders subscribe to `isStale` instead; a
 * subscription cannot help a tap landing in the same tick as expiry, which
 * is the gap this closes.
 */
function isExpired(response: FiatStepResponse): boolean {
  const expiresAt = expiryTime(response)
  return expiresAt === undefined || expiresAt <= Date.now()
}

function expiryTime(response: FiatStepResponse): number | undefined {
  // A KYC state may be returned before a provider can produce a quote. When
  // it does carry one, the round expires with the price behind it.
  const state = response.state
  const quoteExpiryValue = 'quote' in state ? state.quote?.expiresAt : undefined
  if (quoteExpiryValue === undefined) {
    return undefined
  }
  const quoteExpiry = Date.parse(quoteExpiryValue)
  if (!Number.isFinite(quoteExpiry)) {
    return undefined
  }
  const surfaceExpiryValue =
    'surface' in state ? state.surface?.expiresAt : undefined
  if (surfaceExpiryValue === undefined) {
    return quoteExpiry
  }
  const surfaceExpiry = Date.parse(surfaceExpiryValue)
  return Number.isFinite(surfaceExpiry)
    ? Math.min(quoteExpiry, surfaceExpiry)
    : undefined
}
